import React, { useState } from 'react';
import { TextField, Button, Grid } from '@mui/material';
import LoadingSpinner from '../common/LoadingSpinner';
import BaseForm from './BaseForm';

const ContactForm = () => {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        message: ''
    });

    const [errors, setErrors] = useState({
        name: '',
        email: '',
        phone: '',
        message: ''
    });

    const validateEmail = (email) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    const validateField = (name, value) => {
        switch (name) {
            case 'name':
                return value.trim() === '' ? 'Name is required' : '';
            case 'email':
                if (value.trim() === '') return 'Email is required';
                return !validateEmail(value) ? 'Invalid email format' : '';
            case 'message':
                return value.trim() === '' ? 'Comments are required' : '';
            case 'phone':
                return ''; // Phone is optional
            default:
                return '';
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        // Clear error when user starts typing
        setErrors(prev => ({
            ...prev,
            [name]: ''
        }));
    };

    const handleSubmit = async (token) => {
        // Validate all fields
        const newErrors = {
            name: validateField('name', formData.name),
            email: validateField('email', formData.email),
            phone: validateField('phone', formData.phone),
            message: validateField('message', formData.message)
        };

        setErrors(newErrors);

        // Check if there are any errors
        const hasErrors = Object.values(newErrors).some(error => error !== '');

        if (hasErrors) {
            return {
                success: false,
                validationError: true
            };
        }

        const response = await fetch(`${process.env.PUBLIC_URL}/send-email.php`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                ...formData,
                formType: 'contact',
                captchaToken: token
            })
        });

        const result = await response.json();
        
        if (result.success) {
            // Reset form
            setFormData({
                name: '',
                email: '',
                phone: '',
                message: ''
            });
        }
        
        return result;
    };

    return (
        <BaseForm
            onSubmit={handleSubmit}
            title="Thank you for your interest in MacGillis Global Academy!"
            subtitle="Have any Questions or Comments? We would love to hear from you. Please fill out the form below and our team will contact you shortly."
        >
            {({ isSubmitting }) => (
                <>
                    <Grid container spacing={2}>
                        <Grid size={{ xs: 12, sm: 12, md: 5 }}>
                            <TextField
                                fullWidth
                                label="First & Last Name"
                                name="name"
                                value={formData.name}
                                onChange={handleChange}
                                required
                                error={!!errors.name}
                                helperText={errors.name}
                                disabled={isSubmitting}
                                sx={{ width: '100%', boxSizing: 'border-box' }}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, md: 5 }}>
                            <TextField
                                fullWidth
                                label="Email Address"
                                name="email"
                                type="email"
                                value={formData.email}
                                onChange={handleChange}
                                required
                                error={!!errors.email}
                                helperText={errors.email}
                                disabled={isSubmitting}
                                inputProps={{
                                    'aria-label': 'Email address',
                                    'data-testid': 'email-input'
                                }}
                                sx={{ width: '100%', boxSizing: 'border-box' }}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                            <TextField
                                fullWidth
                                label="Phone Number"
                                name="phone"
                                value={formData.phone}
                                onChange={handleChange}
                                error={!!errors.phone}
                                helperText={errors.phone}
                                disabled={isSubmitting}
                                sx={{ width: '100%', boxSizing: 'border-box' }}
                            />
                        </Grid>
                    </Grid>
                    <TextField
                        fullWidth
                        label="Comments"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        margin="normal"
                        required
                        multiline
                        rows={4}
                        error={!!errors.message}
                        helperText={errors.message}
                        disabled={isSubmitting}
                        sx={{ width: '100%', boxSizing: 'border-box' }}
                    />
                    <Button
                        type="submit"
                        variant="contained"
                        color="secondary"
                        disabled={isSubmitting}
                        sx={{ mt: 2 }}
                    >
                        {isSubmitting ? <LoadingSpinner size={20} /> : 'Submit Now'}
                    </Button>
                </>
            )}
        </BaseForm>
    );
};

export default ContactForm; 